/* eslint-disable max-lines, max-lines-per-function */

const MARKER_BLUE = "#2563EB";
const MARKER_WHITE = "#FFFFFF";
const MARKER_SHADOW = "0 10px 15px -3px rgb(0 0 0 / 0.12), 0 4px 6px -4px rgb(0 0 0 / 0.12)";

function getSvgMarkupFromTemplate(selector) {
  const $el = document.querySelector(selector);
  const markup = $el?.innerHTML?.trim();
  return markup || null;
}

function setMarkerSvgAttrs($svg, { width, height, color }) {
  if (!$svg) return;
  const $svgEl = $svg;
  $svgEl.setAttribute("width", String(width));
  $svgEl.setAttribute("height", String(height));
  $svgEl.setAttribute("role", "img");
  $svgEl.setAttribute("aria-hidden", "true");
  $svgEl.setAttribute("focusable", "false");
  if (color) $svgEl.style.color = color;
}

function createMarkerBaseEl({ testId, sizePx, borderPx, background, borderColor, shadow }) {
  const $el = document.createElement("div");
  $el.dataset.testid = testId;
  $el.style.width = `${sizePx}px`;
  $el.style.height = `${sizePx}px`;
  $el.style.borderRadius = "9999px";
  $el.style.background = background;
  $el.style.display = "flex";
  $el.style.alignItems = "center";
  $el.style.justifyContent = "center";
  $el.style.boxShadow = shadow;
  $el.style.border = `${borderPx}px solid ${borderColor}`;
  return $el;
}

const ROUTE_STROKE_WEIGHT_PX = 6;
const ROUTE_ANIMATION_DURATION_MS = 4400;
const ROUTE_ANIMATION_STEPS = 90;

let routesLibPromise = null;
function getRoutesLib(maps) {
  if (!maps?.importLibrary) return null;
  if (!routesLibPromise) {
    routesLibPromise = maps.importLibrary("routes").catch(() => null);
  }
  return routesLibPromise;
}

function isValidLatLngLiteral(value) {
  return value && Number.isFinite(value.lat) && Number.isFinite(value.lng);
}

function latLngToPlain(latLng) {
  if (!latLng) return null;
  if (typeof latLng.lat === "function" && typeof latLng.lng === "function") {
    return { lat: latLng.lat(), lng: latLng.lng() };
  }
  if (typeof latLng.lat === "number" && typeof latLng.lng === "number") {
    return { lat: latLng.lat, lng: latLng.lng };
  }
  return null;
}

async function getDrivingRoutePoints(start, end) {
  const maps = window.google?.maps;
  if (!maps?.importLibrary) return null;
  if (!isValidLatLngLiteral(start) || !isValidLatLngLiteral(end)) return null;

  try {
    const routesLib = await getRoutesLib(maps);
    const Route = routesLib?.Route;
    if (typeof Route?.computeRoutes !== "function") return null;

    const result = await Route.computeRoutes({
      origin: start,
      destination: end,
      travelMode: "DRIVING",
      fields: ["path"]
    });
    const route = result?.routes?.[0];
    const path = route?.path;
    if (!Array.isArray(path) || path.length < 2) return null;

    const points = path.map(latLngToPlain).filter(Boolean);
    return points.length >= 2 ? points : null;
  } catch {
    return null;
  }
}

function getPolylineClass() {
  const Polyline = window.google?.maps?.Polyline;
  return typeof Polyline === "function" ? Polyline : null;
}

function interpolatePoints(start, end, steps) {
  const points = [];
  const total = Math.max(2, Number(steps) || 2);
  for (let i = 0; i < total; i += 1) {
    const t = i / (total - 1);
    points.push({
      lat: start.lat + (end.lat - start.lat) * t,
      lng: start.lng + (end.lng - start.lng) * t
    });
  }
  return points;
}

function animatePolylineDraw(polyline, points, options = {}) {
  if (!polyline) return;
  if (!Array.isArray(points) || points.length < 2) return;

  const duration = Math.max(0, Number(options.durationMs) || 0);
  const startAt = window.performance?.now?.() ?? Date.now();
  const isCancelled = typeof options.isCancelled === "function" ? options.isCancelled : null;

  let drawnCount = 0;

  function frame(now) {
    if (isCancelled?.()) return;
    const current = now ?? window.performance?.now?.() ?? Date.now();
    const elapsed = current - startAt;
    const progress = duration === 0 ? 1 : Math.min(1, elapsed / duration);

    const nextCount = Math.max(2, Math.floor(progress * points.length));

    // Only push a new path when the visible point count actually changes.
    if (nextCount !== drawnCount) {
      drawnCount = nextCount;
      polyline.setPath(points.slice(0, nextCount));
    }

    if (progress < 1) {
      window.requestAnimationFrame(frame);
    }
  }

  // Start with no visible line; it appears as the path grows.
  polyline.setPath([]);
  window.requestAnimationFrame(frame);
}

function scheduleRouteAnimation({ map, polyline, points, durationMs, isCancelled }) {
  let started = false;
  let tilesListener = null;
  let timeoutId = null;

  const cleanup = () => {
    tilesListener?.remove?.();
    tilesListener = null;
    if (timeoutId) window.clearTimeout(timeoutId);
    timeoutId = null;
  };

  const startAnimation = () => {
    if (started) return;
    started = true;
    // Remove scheduling triggers immediately so the tilesloaded listener
    // doesn't linger on the map for subsequent tile loads.
    cleanup();
    animatePolylineDraw(polyline, points, { durationMs, isCancelled });
  };

  // Prefer starting after the map tiles render; fall back to a short timeout.
  if (typeof map?.addListener === "function") {
    tilesListener = map.addListener("tilesloaded", startAnimation);
  }
  timeoutId = window.setTimeout(startAnimation, 700);

  return cleanup;
}

export default async function animateRouteOnMap(options) {
  const { map, start, end, routeMode, strokeColor } = options || {};
  const Polyline = getPolylineClass();
  if (!Polyline || !map || !start || !end) return { didRender: false, cleanup: null };

  const points =
    routeMode === "straight"
      ? interpolatePoints(start, end, ROUTE_ANIMATION_STEPS)
      : await getDrivingRoutePoints(start, end);

  if (!points) return { didRender: false, cleanup: null };

  const polyline = new Polyline({
    map,
    path: [],
    geodesic: true,
    strokeColor,
    strokeOpacity: 1,
    strokeWeight: ROUTE_STROKE_WEIGHT_PX
  });

  let cancelled = false;
  const cleanupSchedule = scheduleRouteAnimation({
    map,
    polyline,
    points,
    durationMs: ROUTE_ANIMATION_DURATION_MS,
    isCancelled: () => cancelled
  });

  const cleanup = () => {
    cancelled = true;
    cleanupSchedule?.();
    polyline.setMap?.(null);
  };

  return { didRender: true, cleanup };
}

export {
  MARKER_BLUE,
  MARKER_WHITE,
  MARKER_SHADOW,
  getSvgMarkupFromTemplate,
  setMarkerSvgAttrs,
  createMarkerBaseEl
};
